import React, { useState } from 'react';
import { WidgetTemplate } from '../../types';
import { catalogMap, getTagLabel } from '../../components/widgets/catalog';

function formatRelativeDate(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString();
}

interface WidgetTableRowProps {
  template: WidgetTemplate;
  canDelete: boolean;
  exportingId: string | null;
  externalWidgets: string[];
  microsoftConnected?: boolean;
  onPreview: (template: WidgetTemplate) => void;
  onEdit: (template: WidgetTemplate) => void;
  onExport: (id: string) => void;
  onDelete: (id: string) => void;
}

const MAX_VISIBLE_TAGS = 3;

const WidgetTableRow: React.FC<WidgetTableRowProps> = ({
  template,
  canDelete,
  exportingId,
  externalWidgets,
  microsoftConnected = false,
  onPreview,
  onEdit,
  onExport,
  onDelete,
}) => {
  const [hovered, setHovered] = useState(false);
  const entry = catalogMap.get(template.type);
  const tags = entry?.tags ?? [];
  const hasOverflow = tags.length > MAX_VISIBLE_TAGS || (template.description?.length ?? 0) > 60;

  return (
    <React.Fragment>
      <tr
        className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors group cursor-pointer"
        onClick={() => onPreview(template)}
        onMouseEnter={() => hasOverflow && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Widget name */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="font-medium text-neutral-900 group-hover:text-primary-600 transition-colors">
              {template.name}
            </span>
          </div>
        </td>

        {/* Credentials indicator */}
        <td className="px-6 py-4">
          {(() => {
            const needsCreds = entry?.tags.includes('requires-credentials');
            if (!needsCreds) {
              return <span className="text-xs text-neutral-400">Not required</span>;
            }
            // Check data-provider credentials
            const dpWidget = entry?.definition.dataProvider?.widget;
            const isDpConnected = dpWidget && externalWidgets.includes(dpWidget);
            // Check Microsoft OAuth credentials (for Excel, Outlook, etc.)
            const isMsWidget = entry?.category === 'microsoft';
            const isConnected = isDpConnected || (isMsWidget && microsoftConnected);
            if (isConnected) {
              return (
                <span className="text-xs text-success-600">
                  Connected
                </span>
              );
            }
            return (
              <span className="text-xs text-warning-600">
                Required
              </span>
            );
          })()}
        </td>

        {/* Updated */}
        <td className="px-6 py-4 hidden md:table-cell">
          <span className="text-xs text-neutral-400">
            {formatRelativeDate(entry?.definition.meta.lastUpdated || template.updatedAt)}
          </span>
        </td>

        {/* Tags */}
        <td className="px-6 py-4 hidden md:table-cell">
          <div className={`flex items-center gap-1 ${hovered ? 'flex-wrap' : 'flex-nowrap overflow-hidden'}`}>
            {(hovered ? tags : tags.slice(0, MAX_VISIBLE_TAGS)).map(tag => (
              <span key={tag} className="inline-block px-1.5 py-0.5 rounded text-[10px] text-neutral-500 bg-neutral-50 border border-neutral-200 whitespace-nowrap flex-shrink-0">
                {getTagLabel(tag)}
              </span>
            ))}
            {!hovered && tags.length > MAX_VISIBLE_TAGS && (
              <span className="inline-block px-1.5 py-0.5 text-[10px] text-neutral-400 whitespace-nowrap flex-shrink-0">
                +{tags.length - MAX_VISIBLE_TAGS}
              </span>
            )}
          </div>
        </td>

        {/* Description */}
        <td className="px-6 py-4 hidden lg:table-cell max-w-xs">
          {template.description ? (
            <span className={`text-sm text-neutral-500 block ${hovered ? 'whitespace-pre-wrap' : 'truncate'}`}>
              {template.description}
            </span>
          ) : (
            <span className="text-xs text-neutral-400">-</span>
          )}
        </td>

        {/* Empty cell for alignment with header */}
        <td className="hidden lg:table-cell w-16" />
      </tr>
    </React.Fragment>
  );
};

export default WidgetTableRow;
export { formatRelativeDate };
